import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatContactDisplayName, type BezoekersbestandEntry, type User } from "@domaddress/shared";
import BezoekersFormModal from "../components/BezoekersFormModal";
import BezoekerListRow from "../components/BezoekerListRow";
import ViewModeToggle from "../components/ViewModeToggle";
import PaginationBar from "../components/PaginationBar";
import { useViewMode } from "../hooks/useViewMode";
import HelpHint from "../components/HelpHint";
import {
  useVcardEmailSend,
  VcardEmailToolbarButton,
  VcardEmailAside,
} from "../components/VcardEmailSendParts";
import { DOMINICUSGANGERS_API } from "../lib/contactApiPaths";
import { bezoekerEntryToContact } from "../lib/bezoekerEntryToContact";

interface Props {
  user: User | null;
}

function ToggleChip({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className="group-badge"
      style={{
        opacity: active ? 1 : 0.45,
        fontSize: "0.75rem",
      }}
      title={label}
    >
      {active ? "✓ " : "— "}
      {label}
    </span>
  );
}

export default function BezoekersbestandPage({ user }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useViewMode("dominicusgangers");
  const [entries, setEntries] = useState<BezoekersbestandEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [wantsNewsletter, setWantsNewsletter] = useState("");
  const [wantsActivities, setWantsActivities] = useState("");
  const [willingVolunteer, setWillingVolunteer] = useState("");
  const [linkedFilter, setLinkedFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState<"" | "without" | "with">("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BezoekersbestandEntry | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search, cityFilter, countryFilter, wantsNewsletter, wantsActivities, willingVolunteer, linkedFilter, perPage, emailFilter]);

  const isCoordinator = user?.app_role === "vrijwilliger_coordinator";
  const vcard = useVcardEmailSend(DOMINICUSGANGERS_API);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (search.trim()) params.search = search.trim();
      if (cityFilter.trim()) params.city = cityFilter.trim();
      if (countryFilter.trim()) params.country = countryFilter.trim();
      if (wantsNewsletter === "1" || wantsNewsletter === "0") params.wants_newsletter = wantsNewsletter;
      if (wantsActivities === "1" || wantsActivities === "0") params.wants_activities_info = wantsActivities;
      // Coördinatoren zien server-side al alleen vrijwilligers; "nee" zou lege lijst geven (1 AND 0).
      if (!isCoordinator && (willingVolunteer === "1" || willingVolunteer === "0")) {
        params.willing_volunteer = willingVolunteer;
      }
      if (linkedFilter === "1" || linkedFilter === "0") params.linked = linkedFilter;
      if (emailFilter) params.has_email = emailFilter;
      const res = await api.get("/api/bezoekersbestand", { params });
      setEntries(res.data.data);
      setTotal(typeof res.data.total === "number" ? res.data.total : res.data.data.length);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    perPage,
    search,
    cityFilter,
    countryFilter,
    wantsNewsletter,
    wantsActivities,
    willingVolunteer,
    linkedFilter,
    isCoordinator,
    emailFilter,
  ]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  /** Open een kaart via ?contactId= (zelfde param als andere contactlijsten; deeplink uit activiteitenlog). */
  useEffect(() => {
    const raw = searchParams.get("contactId");
    const id = raw != null && raw !== "" ? Number.parseInt(raw, 10) : NaN;
    if (!Number.isInteger(id) || id < 1) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ data: BezoekersbestandEntry }>(`/api/bezoekersbestand/${id}`);
        if (cancelled) return;
        setEditing(res.data.data);
        setShowForm(true);
      } catch {
        if (!cancelled) {
          setEditing(null);
          setShowForm(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  async function handleDelete(id: number) {
    if (!confirm("Deze dominicusganger verwijderen?")) return;
    try {
      await api.delete(`/api/bezoekersbestand/${id}`);
      await fetchEntries();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      alert(ax.response?.data?.error ?? "Verwijderen mislukt");
    }
  }

  const isSuperAdmin = !!user?.is_super_admin;
  const canWriteBezoekers = !isCoordinator;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title-row" style={{ marginBottom: 2 }}>
            <h2 className="page-title" style={{ margin: 0 }}>
              Dominicusgangers
            </h2>
            <HelpHint label="Uitleg: dominicusgangers">
              <span>
                Wie als <strong>lid</strong> of <strong>voorzitter</strong> aan een groep wordt toegevoegd, wordt waar
                nodig automatisch op een medewerkerskaart gezet (vrijwilliger of personeel) — dat vervangt het oude
                handmatig “promoveren” vanaf dit scherm.{" "}
              </span>
              {isCoordinator && !isSuperAdmin && (
                <span>
                  <strong>Alleen lezen</strong> voor coördinatoren: je ziet mensen die aangegeven hebben vrijwilliger te
                  willen zijn.{" "}
                </span>
              )}
              {!isSuperAdmin && !isCoordinator && (
                <span>Dominicusgangers komen uit het contactformulier of import; bovenin dit scherm telt de app mee. </span>
              )}
            </HelpHint>
          </div>
          <p className="page-subtitle">
            {loading ? "…" : `${total} ${total === 1 ? "dominicusganger" : "dominicusgangers"}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {user && <VcardEmailToolbarButton b={vcard} />}
          {canWriteBezoekers && (
            <Link to="/dominicusgangers/import" className="btn-secondary">
              <span>⇪</span> Importeren uit e-mail
            </Link>
          )}
          <Link to="/vrijwilligers" className="btn-secondary">
            Naar vrijwilligers
          </Link>
          {canWriteBezoekers && (
            <button
              className="btn-primary"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
            >
              <span>+</span> Nieuwe dominicusganger
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <div className="filters-start">
          <div className="search-box">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Zoek (naam, e-mail, stad, adres…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <input
            type="text"
            className="filter-select"
            style={{ minWidth: "110px" }}
            placeholder="Plaats…"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            aria-label="Filter op plaats"
          />
          <input
            type="text"
            className="filter-select"
            style={{ minWidth: "110px" }}
            placeholder="Land…"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            aria-label="Filter op land"
          />
          <select
            className="filter-select"
            aria-label="Filter op e-mailadres"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value as "" | "without" | "with")}
          >
            <option value="">E-mail: alle</option>
            <option value="without">Zonder e-mail</option>
            <option value="with">Met e-mail</option>
          </select>
          <select
            className="filter-select"
            value={wantsNewsletter}
            onChange={(e) => setWantsNewsletter(e.target.value)}
            aria-label="Filter nieuwsbrief"
          >
            <option value="">Nieuwsbrief: alle</option>
            <option value="1">Nieuwsbrief: ja</option>
            <option value="0">Nieuwsbrief: nee</option>
          </select>
          <select
            className="filter-select"
            value={wantsActivities}
            onChange={(e) => setWantsActivities(e.target.value)}
            aria-label="Filter activiteiten"
          >
            <option value="">Activiteiten: alle</option>
            <option value="1">Activiteiten: ja</option>
            <option value="0">Activiteiten: nee</option>
          </select>
          {isCoordinator ? (
            <span
              className="filter-select"
              style={{
                display: "inline-flex",
                alignItems: "center",
                minWidth: "160px",
                fontSize: "0.875rem",
                color: "var(--text-muted, #666)",
                border: "1px solid var(--border, #e5e7eb)",
                borderRadius: 8,
                padding: "0.35rem 0.6rem",
                background: "var(--card-bg, #fff)",
              }}
              title="Voor jouw rol toont dit scherm alleen dominicusgangers die vrijwilliger willen worden"
            >
              Alleen vrijwilligers
            </span>
          ) : (
            <select
              className="filter-select"
              value={willingVolunteer}
              onChange={(e) => setWillingVolunteer(e.target.value)}
              aria-label="Filter vrijwilliger"
            >
              <option value="">Vrijwilliger: alle</option>
              <option value="1">Vrijwilliger: ja</option>
              <option value="0">Vrijwilliger: nee</option>
            </select>
          )}
          <select
            className="filter-select"
            value={linkedFilter}
            onChange={(e) => setLinkedFilter(e.target.value)}
            aria-label="Filter gekoppeld contact"
          >
            <option value="">Koppeling: alle</option>
            <option value="1">Met contact</option>
            <option value="0">Zonder contact</option>
          </select>
        </div>
        <div className="filters-end">
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
      {loading ? (
        viewMode === "tiles" ? (
          <div className="loading-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton-card" />
            ))}
          </div>
        ) : (
          <div className="loading-list">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="skeleton-card" />
            ))}
          </div>
        )
      ) : total === 0 ? (
        <div className="empty-state">
          <p>Nog geen dominicusgangers. Voeg iemand toe of pas het zoekfilter aan.</p>
          {canWriteBezoekers && (
            <button
              className="btn-primary"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
            >
              Eerste dominicusganger toevoegen
            </button>
          )}
        </div>
      ) : (
        <>
      {entries.length === 0 ? (
        <div className="empty-state">
          <p>Geen resultaten op deze pagina. Ga naar de eerste pagina of pas filters aan.</p>
          <button type="button" className="btn-secondary" onClick={() => setPage(1)}>
            Eerste pagina
          </button>
        </div>
      ) : viewMode === "tiles" ? (
        <div className="contacts-grid">
          {entries.map((b) => (
            <div key={b.id} className="contact-card">
              <div className="contact-card-header">
                <div className="contact-avatar">
                  <span>
                    {(b.first_name[0] ?? "").toUpperCase()}
                    {(b.last_name[0] ?? "").toUpperCase()}
                  </span>
                </div>
                <div className="contact-actions">
                  {vcard.sendListOpen && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => vcard.addToSendList(bezoekerEntryToContact(b))}
                      title="Toevoegen aan zend-contacten"
                    >
                      Toevoegen →
                    </button>
                  )}
                {canWriteBezoekers && (
                  <>
                    <button
                      type="button"
                      className="btn-icon"
                      title="Bewerken"
                      onClick={() => {
                        setEditing(b);
                        setShowForm(true);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="btn-icon btn-icon-danger"
                      title="Verwijderen"
                      onClick={() => handleDelete(b.id)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3,6 5,6 21,6" />
                        <path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6" />
                        <path d="M10,11v6" />
                        <path d="M14,11v6" />
                        <path d="M9,6V4h6v2" />
                      </svg>
                    </button>
                  </>
                )}
                </div>
              </div>
              <div className="contact-info">
                <h3 className="contact-name">
                  {formatContactDisplayName(b)}
                  <span className="page-subtitle" style={{ fontSize: "0.75rem", marginLeft: 6, fontWeight: 400 }} title="Uniek kaartnummer">
                    #{b.id}
                  </span>
                </h3>
                {b.birth_date && (
                  <p className="page-subtitle" style={{ margin: "0 0 4px" }}>
                    Geb. {b.birth_date}
                  </p>
                )}
                {b.email && (
                  <a href={`mailto:${b.email}`} className="contact-detail">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m22,7-10,7L2,7" />
                    </svg>
                    {b.email}
                  </a>
                )}
                {b.phone && (
                  <a href={`tel:${b.phone}`} className="contact-detail">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 1.12h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6 6l1.55-1.55a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    {b.phone}
                  </a>
                )}
                <div className="contact-groups" style={{ marginTop: "0.5rem", flexWrap: "wrap" }}>
                  <ToggleChip active={b.wants_newsletter} label="Nieuwsbrief" />
                  <ToggleChip active={b.wants_activities_info} label="Activiteiten" />
                  <ToggleChip active={b.willing_volunteer} label="Vrijwilliger" />
                </div>
                {b.linked_contact_id != null && (
                  <p style={{ marginTop: "0.75rem", fontSize: "0.85rem" }}>
                    <span className="group-badge" style={{ background: "var(--primary)", color: "white" }}>
                      Gekoppeld aan contact #{b.linked_contact_id}
                    </span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="data-list">
          {entries.map((b) => (
            <BezoekerListRow
              key={b.id}
              b={b}
              canWrite={canWriteBezoekers}
              onEdit={() => {
                setEditing(b);
                setShowForm(true);
              }}
              onDelete={() => handleDelete(b.id)}
              onAddToSendList={
                vcard.sendListOpen
                  ? () => vcard.addToSendList(bezoekerEntryToContact(b))
                  : undefined
              }
            />
          ))}
        </div>
      )}
          <PaginationBar
            page={page}
            perPage={perPage}
            total={total}
            itemLabel="dominicusgangers"
            onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        </>
      )}
        </div>

        <VcardEmailAside b={vcard} />
      </div>

      {showForm && (
        <BezoekersFormModal
          entry={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
            if (searchParams.get("contactId")) {
              const next = new URLSearchParams(searchParams);
              next.delete("contactId");
              setSearchParams(next, { replace: true });
            }
          }}
          onSaved={() => {
            setShowForm(false);
            setEditing(null);
            if (searchParams.get("contactId")) {
              const next = new URLSearchParams(searchParams);
              next.delete("contactId");
              setSearchParams(next, { replace: true });
            }
            fetchEntries();
          }}
        />
      )}
    </div>
  );
}
